/**
 * The generative-provider boundary (spec §37-41, §59, §66). Everything that
 * lets HipNumber swap Grok for OpenAI for Gemini without touching a
 * tenant-facing concept lives behind these contracts.
 *
 *  - Capability registries describe what a provider/model CAN do, in
 *    domain terms (aspect ratios, image editing, first/last frame), so the
 *    router reasons about capabilities, not vendor names.
 *  - The router is a PURE function: given a required capability set, an aspect
 *    ratio, a cost ceiling and a policy, it picks a provider from the
 *    candidates. No tenant UI selects a provider (spec §40, §96).
 *  - The error taxonomy classifies failures and says which are retryable, so a
 *    worker's backoff/fallback logic is one shared decision (spec §41).
 *  - ProviderRun is the audit row (spec §66): provider, model, operation,
 *    cost, external request id, error - never a secret, never a token (§64).
 *
 * The two provider INTERFACES are here as types only; adapters implement them
 * in a providers package. This contract package stays pure and has no adapter.
 */

import { z } from 'zod';
import { MoneySchema, type Money } from './ids.js';

// ---------------------------------------------------------------------------
// Capabilities (spec §39)
// ---------------------------------------------------------------------------

export const ImageProviderCapabilitiesSchema = z
  .object({
    textToImage: z.boolean(),
    imageEditing: z.boolean(),
    referenceImages: z.boolean(),
    transparentBackground: z.boolean(),
    maxReferenceImages: z.number().int().positive().optional(),
    supportedAspectRatios: z.array(z.string()),
    maxWidth: z.number().int().positive().optional(),
    maxHeight: z.number().int().positive().optional(),
    supportsSeed: z.boolean().optional(),
    qualityModes: z.array(z.string()).optional(),
  })
  .strict();
export type ImageProviderCapabilities = z.infer<typeof ImageProviderCapabilitiesSchema>;

export const VideoProviderCapabilitiesSchema = z
  .object({
    textToVideo: z.boolean(),
    imageToVideo: z.boolean(),
    videoEditing: z.boolean(),
    nativeAudio: z.boolean(),
    referenceImages: z.boolean().optional(),
    firstLastFrame: z.boolean().optional(),
    supportedAspectRatios: z.array(z.string()),
    supportedDurations: z.array(z.number().int().positive()),
  })
  .strict();
export type VideoProviderCapabilities = z.infer<typeof VideoProviderCapabilitiesSchema>;

// ---------------------------------------------------------------------------
// Cost estimate (spec §37, §59)
// ---------------------------------------------------------------------------

export const CostEstimateSchema = z
  .object({
    /** Normalised HipNumber cost, for routing and cost guards. */
    estimated: MoneySchema,
    /** Optional provider-native figure, for the audit row. */
    providerNative: MoneySchema.optional(),
  })
  .strict();
export type CostEstimate = z.infer<typeof CostEstimateSchema>;

// ---------------------------------------------------------------------------
// Provider interfaces (types only - adapters live elsewhere)
// ---------------------------------------------------------------------------

export interface ImageGenerationInput {
  prompt: string;
  negative?: string[];
  aspectRatio: string;
  width?: number;
  height?: number;
  referenceStorageKeys?: string[];
  seed?: number;
  parameters?: Record<string, unknown>;
}

export interface ImageGenerationResult {
  storageKey: string;
  width: number;
  height: number;
  externalRequestId?: string;
  cost?: Money;
}

export interface ImageEditInput extends ImageGenerationInput {
  baseStorageKey: string;
}

export interface ImageGenerationProvider {
  readonly providerKey: string;
  capabilities(): ImageProviderCapabilities;
  estimateCost(input: ImageGenerationInput): Promise<CostEstimate>;
  generate(input: ImageGenerationInput): Promise<ImageGenerationResult>;
  edit?(input: ImageEditInput): Promise<ImageGenerationResult>;
}

export interface VideoGenerationInput {
  prompt?: string;
  aspectRatio: string;
  durationMs: number;
  referenceStorageKeys?: string[];
  parameters?: Record<string, unknown>;
}

export interface VideoGenerationResult {
  storageKey: string;
  width: number;
  height: number;
  durationMs: number;
  externalRequestId?: string;
  cost?: Money;
}

export interface ImageToVideoInput extends VideoGenerationInput {
  imageStorageKey: string;
}

export interface VideoGenerationProvider {
  readonly providerKey: string;
  capabilities(): VideoProviderCapabilities;
  estimateCost(input: VideoGenerationInput): Promise<CostEstimate>;
  generate(input: VideoGenerationInput): Promise<VideoGenerationResult>;
  imageToVideo?(input: ImageToVideoInput): Promise<VideoGenerationResult>;
  edit?(input: VideoGenerationInput): Promise<VideoGenerationResult>;
}

// ---------------------------------------------------------------------------
// Provider run audit (spec §66)
// ---------------------------------------------------------------------------

export const ProviderRunSchema = z
  .object({
    id: z.string().min(1),
    tenantId: z.string().min(1),
    campaignId: z.string().min(1),
    assetId: z.string().min(1).optional(),
    providerKey: z.string().min(1),
    modelKey: z.string().min(1).optional(),
    operation: z.string().min(1),
    startedAt: z.string().datetime({ offset: true }),
    finishedAt: z.string().datetime({ offset: true }).optional(),
    status: z.enum(['SUCCESS', 'FAILED']),
    cost: MoneySchema.optional(),
    externalRequestId: z.string().optional(),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
  })
  .strict();
export type ProviderRun = z.infer<typeof ProviderRunSchema>;

// ---------------------------------------------------------------------------
// Error taxonomy (spec §41)
// ---------------------------------------------------------------------------

export const PROVIDER_ERROR_CLASSES = [
  'RATE_LIMIT',
  'CONTENT_REJECTED',
  'PROVIDER_DOWN',
  'TIMEOUT',
  'INVALID_INPUT',
  'UNSUPPORTED_FORMAT',
  'AUTH_ERROR',
  'COST_LIMIT',
  'UNKNOWN',
] as const;
export const ProviderErrorClassSchema = z.enum(PROVIDER_ERROR_CLASSES);
export type ProviderErrorClass = z.infer<typeof ProviderErrorClassSchema>;

/** Transient classes worth a bounded retry; the rest are terminal for the run
 *  (spec §41: retry only when safe, never mutate truth to make one succeed). */
const RETRYABLE: ReadonlySet<ProviderErrorClass> = new Set(['RATE_LIMIT', 'PROVIDER_DOWN', 'TIMEOUT']);

export function isRetryable(cls: ProviderErrorClass): boolean {
  return RETRYABLE.has(cls);
}

/** Map a raw provider failure onto the taxonomy. Adapters pass what they have -
 *  an HTTP status, a provider code, a message - and get one class back, so the
 *  worker's retry/fallback decision is uniform across vendors. */
export function classifyProviderError(input: { status?: number; code?: string; message?: string }): ProviderErrorClass {
  const code = (input.code ?? '').toLowerCase();
  const msg = (input.message ?? '').toLowerCase();
  const status = input.status;

  if (status === 429 || code.includes('rate') || msg.includes('rate limit')) return 'RATE_LIMIT';
  if (status === 401 || status === 403 || code.includes('auth') || msg.includes('unauthorized')) return 'AUTH_ERROR';
  if (status === 408 || code.includes('timeout') || msg.includes('timed out') || msg.includes('timeout')) return 'TIMEOUT';
  if (code.includes('content') || msg.includes('safety') || msg.includes('rejected') || msg.includes('policy')) return 'CONTENT_REJECTED';
  if (code.includes('cost') || msg.includes('budget') || msg.includes('quota exceeded')) return 'COST_LIMIT';
  if (code.includes('format') || msg.includes('unsupported')) return 'UNSUPPORTED_FORMAT';
  if (status === 400 || code.includes('invalid') || msg.includes('invalid')) return 'INVALID_INPUT';
  if (status !== undefined && status >= 500) return 'PROVIDER_DOWN';
  if (msg.includes('econnrefused') || msg.includes('unavailable')) return 'PROVIDER_DOWN';
  return 'UNKNOWN';
}

// ---------------------------------------------------------------------------
// Router (spec §40)
// ---------------------------------------------------------------------------

export const ROUTE_POLICIES = ['ECONOMY', 'FAST', 'BALANCED', 'BEST'] as const;
export const RoutePolicySchema = z.enum(ROUTE_POLICIES);
export type RoutePolicy = z.infer<typeof RoutePolicySchema>;

/** A provider/model the router may choose from, with the facts it ranks on.
 *  `speedRank` and `qualityRank`: lower is faster / better. */
export interface ProviderCandidate {
  providerKey: string;
  modelKey?: string;
  mediaType: 'IMAGE' | 'VIDEO';
  capabilities: ImageProviderCapabilities | VideoProviderCapabilities;
  healthy: boolean;
  estimatedCost?: Money;
  speedRank: number;
  qualityRank: number;
}

export interface ProviderRouteInput {
  mediaType: 'IMAGE' | 'VIDEO';
  /** Boolean capability flags that MUST be true (e.g. 'imageEditing'). */
  requiredCapabilities?: string[];
  aspectRatio: string;
  policy: RoutePolicy;
  /** Reject candidates whose normalised estimate exceeds this. */
  costCeiling?: Money;
}

export interface ProviderRouteDecision {
  providerKey: string;
  modelKey?: string;
  reasonCode: string;
  estimatedCost?: Money;
}

export type ProviderRouteResult =
  | { ok: true; decision: ProviderRouteDecision }
  | { ok: false; reasonCode: string };

function hasBooleanCapability(caps: ProviderCandidate['capabilities'], name: string): boolean {
  return (caps as Record<string, unknown>)[name] === true;
}

/**
 * Pick a provider for one creative. Pure: same inputs, same decision. Filters
 * by media type, health, aspect-ratio support, required capabilities and cost
 * ceiling, then orders the survivors by the policy. Returns a reason code
 * either way so a diagnostic view (spec §65) can explain the choice, and an
 * actionable failure when nothing qualifies (spec §41, §78 - never a generic
 * "something went wrong").
 */
export function routeProvider(input: ProviderRouteInput, candidates: readonly ProviderCandidate[]): ProviderRouteResult {
  const required = input.requiredCapabilities ?? [];

  const eligible = candidates.filter((c) => {
    if (c.mediaType !== input.mediaType) return false;
    if (!c.healthy) return false;
    if (!c.capabilities.supportedAspectRatios.includes(input.aspectRatio)) return false;
    if (!required.every((cap) => hasBooleanCapability(c.capabilities, cap))) return false;
    if (input.costCeiling && c.estimatedCost && !withinCeiling(c.estimatedCost, input.costCeiling)) return false;
    return true;
  });

  if (eligible.length === 0) return { ok: false, reasonCode: 'NO_ELIGIBLE_PROVIDER' };

  const ordered = [...eligible].sort((a, b) => compareByPolicy(a, b, input.policy));
  const chosen = ordered[0]!;
  const decision: ProviderRouteDecision = {
    providerKey: chosen.providerKey,
    reasonCode: eligible.length === 1 ? 'ONLY_ELIGIBLE' : `POLICY_${input.policy}`,
  };
  if (chosen.modelKey !== undefined) decision.modelKey = chosen.modelKey;
  if (chosen.estimatedCost !== undefined) decision.estimatedCost = chosen.estimatedCost;
  return { ok: true, decision };
}

function costMinor(m: Money | undefined): number {
  return m ? m.amountMinor : Number.POSITIVE_INFINITY;
}

function withinCeiling(cost: Money, ceiling: Money): boolean {
  // Only comparable in the same currency; a mismatch is treated as over.
  return cost.currency === ceiling.currency && cost.amountMinor <= ceiling.amountMinor;
}

function compareByPolicy(a: ProviderCandidate, b: ProviderCandidate, policy: RoutePolicy): number {
  switch (policy) {
    case 'ECONOMY':
      return costMinor(a.estimatedCost) - costMinor(b.estimatedCost) || a.speedRank - b.speedRank;
    case 'FAST':
      return a.speedRank - b.speedRank || costMinor(a.estimatedCost) - costMinor(b.estimatedCost);
    case 'BEST':
      return a.qualityRank - b.qualityRank || costMinor(a.estimatedCost) - costMinor(b.estimatedCost);
    case 'BALANCED':
      return (
        a.speedRank + a.qualityRank - (b.speedRank + b.qualityRank) ||
        costMinor(a.estimatedCost) - costMinor(b.estimatedCost)
      );
  }
}
